import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';

import DataTypePill, {
  DataTypePillType,
} from 'app/components/CommonStyled/DataTypePill/DataTypePill';
import InfoTooltip from 'app/components/CommonStyled/InfoTooltip';
import PersonAvatar from 'app/components/CommonStyled/PersonAvatar';
import Total from 'app/components/CommonStyled/Total';
import { TotalDataType } from 'app/components/CommonStyled/Total/interfaces';
import UserAvatar from 'app/components/CommonStyled/UserAvatar';
import {
  ClientIcon,
  IndustryIcon,
  ProjectIcon,
  SkillIcon,
} from 'app/components/DetailsPages/shared/CommonGround/Icons';
import Tooltip from 'app/components/Tooltip';
import { CloseIcon, SearchIconSmall, CubeIcon, KeyIcon } from 'app/components/shared/icons';
import PulseBlackIcon from 'app/components/shared/icons/pulse-black-icon';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import { ICollectiveDirectoryPersonDrawerResponse, Match } from 'interfaces';
import { foundSelector } from 'store/reducers/nlp/nlp-reducer';
import {
  groupedFiltersSelector,
  IPeopleUuids,
  NLPPeopleGroupsSelector,
  uuidsSelector,
} from 'store/reducers/people/people-reducer';
import { ThemeProvider } from 'styled-components';
import {
  htmlElementsArrayToOxford,
  singularOrPlural,
  arrayToOxford,
  experienceMonthsToYears,
} from 'utils';
import {
  oldModelsToNewSelectedCriteria,
  oldModelsToNewSelectedCriteriaTooltipLabel,
} from 'utils/constants';

import {
  CollectiveDirectoryPersonDrawerSearchConnectionsColumn,
  CollectiveDirectoryPersonDrawerSearchConnectionsColumnShowMore,
  CollectiveDirectoryPersonDrawerSearchConnectionsColumnShowMoreText,
  CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle,
  CollectiveDirectoryPersonDrawerSearchConnectionsGrid,
  CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer,
  CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle,
  CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitleNoMatch,
  CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitleNoMatchText,
  CollectiveDirectoryPersonDrawerSearchConnectionsRow,
  CollectiveDirectoryPersonDrawerSearchConnectionsRowShowMore,
  CollectiveDirectoryPersonDrawerSearchConnectionsTitle,
  CollectiveDirectoryPersonDrawerSearchConnectionsWrapper,
  CollectiveDirectoryPersonDrawerSearchMatchesCommonGroundAvatarsContainer,
  CollectiveDirectoryPersonDrawerSearchMatchesCommonGroundWrapper,
  CollectiveDirectoryPersonDrawerSearchMatchesTeamingExperienceWrapper,
  CollectiveDirectoryPersonDrawerSearchMatchesTotalsContainer,
  CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsContainer,
  CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsViewMore,
  CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsWrapper,
  CollectiveDirectoryPersonDrawerSearchConnectionBreakdownContainer,
  CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem,
  CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon,
  CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription,
  CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem,
  CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon,
  CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle,
} from './styled';

const commonGroundTypesToTotalsMap = new Map<string, TotalDataType>([
  ['skills', TotalDataType.SKILLS],
]);

const totalsToWorkExperienceMap = new Map<TotalDataType, string>([
  [TotalDataType.SKILLS, 'skills'],
  [TotalDataType.INDUSTRIES, 'industries'],
  [TotalDataType.PRIOR_CLIENTS, 'clients'],
  [TotalDataType.PROJECTS, 'projects'],
]);

const totalsToDataTypePillMap = new Map<TotalDataType | string, DataTypePillType>([
  [TotalDataType.SKILLS, 'skill'],
  [TotalDataType.INDUSTRIES, 'industry'],
  [TotalDataType.PRIOR_CLIENTS, 'client'],
  [TotalDataType.PROJECTS, 'project'],
  [TotalDataType.PASSIONS, 'passion'],
  [TotalDataType.COMPANIES, 'company'],
  [TotalDataType.CLIENTS, 'client'],
  [TotalDataType.LANGUAGES, 'language'],
  [TotalDataType.COMMUNITIES, 'community'],
  [TotalDataType.CITIES, 'city'],
  ['skills', 'skill'],
  [TotalDataType.COUNTRIES, 'country'],
]);

const dataTypeToSingularTitleMap = new Map<TotalDataType, string>([
  [TotalDataType.SKILLS, 'SKILL'],
  [TotalDataType.INDUSTRIES, 'INDUSTRY'],
  [TotalDataType.PRIOR_CLIENTS, 'CLIENT'],
  [TotalDataType.CLIENTS, 'CLIENT'],
  [TotalDataType.PROJECTS, 'PROJECT'],
  [TotalDataType.PASSIONS, 'PASSION'],
  [TotalDataType.COMPANIES, 'COMPANY'],
  [TotalDataType.INDUSTRIES, 'INDUSTRY'],
  [TotalDataType.COMMUNITIES, 'COMMUNITY'],
  [TotalDataType.CONVENINGS, 'CONVENING'],
  [TotalDataType.LANGUAGES, 'LANGUAGE'],
  [TotalDataType.COUNTRIES, 'COUNTRY'],
  [TotalDataType.CITIES, 'CITY'],
  [TotalDataType.COUNTRIES, 'COUNTRY'],
]);

const dataTypeToPluralTitleMap = new Map<TotalDataType, string>([
  [TotalDataType.SKILLS, 'SKILLS'],
  [TotalDataType.INDUSTRIES, 'INDUSTRIES'],
  [TotalDataType.PRIOR_CLIENTS, 'CLIENTS'],
  [TotalDataType.CLIENTS, 'CLIENTS'],
  [TotalDataType.PROJECTS, 'PROJECTS'],
  [TotalDataType.PASSIONS, 'PASSIONS'],
  [TotalDataType.COMPANIES, 'COMPANIES'],
  [TotalDataType.INDUSTRIES, 'INDUSTRIES'],
  [TotalDataType.COMMUNITIES, 'COMMUNITIES'],
  [TotalDataType.CONVENINGS, 'CONVENINGS'],
  [TotalDataType.LANGUAGES, 'LANGUAGES'],
  [TotalDataType.COUNTRIES, 'COUNTRIES'],
  [TotalDataType.CITIES, 'CITIES'],
  [TotalDataType.COUNTRIES, 'COUNTRIES'],
]);

export default function CollectiveDirectoryPersonDrawerSearchMatches({}) {
  return <></>;
}

CollectiveDirectoryPersonDrawerSearchMatches.WorkExperienceDetails =
  function CollectiveDirectoryPersonDrawerSearchMatchesWorkExperienceDetails({
    person,
  }: {
    person: ICollectiveDirectoryPersonDrawerResponse;
  }) {
    const { push } = useHistory();
    const teamingDrawerWorkExperienceToggledBehavioralFunction = useBehavioral(
      BEHAVIORALS.TEAMING_DRAWER_WORK_EXPERIENCE_TOGGLED
    );

    const [currentlySelected, setCurrentlySelected] = useState<TotalDataType | null>(() => {
      const orderedKeys = [
        { key: 'skills', type: TotalDataType.SKILLS },
        { key: 'industries', type: TotalDataType.INDUSTRIES },
        { key: 'clients', type: TotalDataType.PRIOR_CLIENTS },
        { key: 'projects', type: TotalDataType.PROJECTS },
      ];

      const firstNonEmptyKey = orderedKeys.find(
        ({ key }) => person.work_experience[key] && person.work_experience[key].length > 0
      );
      return firstNonEmptyKey?.type || null;
    });

    const [seeMore, setSeeMore] = useState(false);

    const handleTotalClick = (dataType: TotalDataType) => {
      teamingDrawerWorkExperienceToggledBehavioralFunction({ action_details: dataType });
      setCurrentlySelected(dataType);
    };

    const handleViewMoreClick = () => {
      push(`/person/${person.info.slug}#professional`);
    };

    return (
      <ThemeProvider theme={{ currentlySelected }}>
        <CollectiveDirectoryPersonDrawerSearchMatchesTeamingExperienceWrapper>
          <CollectiveDirectoryPersonDrawerSearchMatchesTotalsContainer>
            {person.work_experience.skills.length > 0 ? (
              <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem
                onClick={() => handleTotalClick(TotalDataType.SKILLS)}
              >
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                  <SkillIcon fill="var(--color-data-skill-foreground)" />
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle
                  currentlySelected={currentlySelected === TotalDataType.SKILLS}
                >
                  {person.work_experience.skills.length}{' '}
                  {singularOrPlural(person.work_experience.skills.length, 'skill', 'skills')}
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle>
              </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem>
            ) : null}
            {person.work_experience.industries.length > 0 ? (
              <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem
                onClick={() => handleTotalClick(TotalDataType.INDUSTRIES)}
              >
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                  <IndustryIcon fill="var(--color-data-industry-foreground)" />
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle
                  currentlySelected={currentlySelected === TotalDataType.INDUSTRIES}
                >
                  {person.work_experience.industries.length}{' '}
                  {singularOrPlural(
                    person.work_experience.industries.length,
                    'industry',
                    'industries'
                  )}
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle>
              </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem>
            ) : null}
            {person.work_experience.clients.length > 0 ? (
              <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem
                onClick={() => handleTotalClick(TotalDataType.PRIOR_CLIENTS)}
              >
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                  <ClientIcon fill="var(--color-data-client-foreground)" />
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle
                  currentlySelected={currentlySelected === TotalDataType.PRIOR_CLIENTS}
                >
                  {person.work_experience.clients.length}{' '}
                  {singularOrPlural(person.work_experience.clients.length, 'client', 'clients')}
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle>
              </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem>
            ) : null}
            {person.work_experience.projects.length > 0 ? (
              <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem
                onClick={() => handleTotalClick(TotalDataType.PROJECTS)}
              >
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                  <ProjectIcon fill="var(--color-data-project-foreground)" />
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemIcon>
                <CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle
                  currentlySelected={currentlySelected === TotalDataType.PROJECTS}
                >
                  {person.work_experience.projects.length}{' '}
                  {singularOrPlural(person.work_experience.projects.length, 'project', 'projects')}
                </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItemTitle>
              </CollectiveDirectoryPersonDrawerSearchMatchesTotalsItem>
            ) : null}
          </CollectiveDirectoryPersonDrawerSearchMatchesTotalsContainer>
          <CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsContainer>
            <CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsWrapper expanded={seeMore}>
              {person.work_experience[totalsToWorkExperienceMap.get(currentlySelected)] &&
              person.work_experience[totalsToWorkExperienceMap.get(currentlySelected)].length > 0
                ? person.work_experience[totalsToWorkExperienceMap.get(currentlySelected)]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .slice(0, seeMore ? undefined : 5)
                    .map((pill, index) => (
                      <DataTypePill
                        key={`${pill.name}-${index}`}
                        type={
                          currentlySelected === TotalDataType.PRIOR_CLIENTS
                            ? pill.company_type
                            : totalsToDataTypePillMap.get(currentlySelected)
                        }
                        title={pill.name}
                      />
                    ))
                : null}
              {person.work_experience[totalsToWorkExperienceMap.get(currentlySelected)] &&
                person.work_experience[totalsToWorkExperienceMap.get(currentlySelected)].length >
                  5 && (
                  <CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsViewMore
                    onClick={() => setSeeMore(!seeMore)}
                  >
                    {seeMore ? 'Show less' : 'Show more'}
                  </CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsViewMore>
                )}
            </CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsWrapper>
          </CollectiveDirectoryPersonDrawerSearchMatchesTotalsPillsContainer>
        </CollectiveDirectoryPersonDrawerSearchMatchesTeamingExperienceWrapper>
      </ThemeProvider>
    );
  };

CollectiveDirectoryPersonDrawerSearchMatches.SearchConnections =
  function CollectiveDirectoryPersonDrawerSearchConnections({
    person,
  }: {
    person: ICollectiveDirectoryPersonDrawerResponse;
  }) {
    const found = useSelector(foundSelector);
    const uuids = useSelector(uuidsSelector);
    const groupedFilters = useSelector(groupedFiltersSelector);
    const nlpPeopleGroups = useSelector(NLPPeopleGroupsSelector);
    const [currentlyShowing, setCurrentlyShowing] = useState<number>(5);

    const currentPersonUuid = useMemo(() => {
      return uuids.find((uuid) => uuid.uuid === person.info.uuid);
    }, [uuids, person]);

    const pulseIsPresent = useMemo<boolean>(() => {
      const pulseGroup = Object.keys(groupedFilters).find((key) =>
        key.toLowerCase().includes('pulse')
      );
      return pulseGroup && person.info.pulse_description !== null;
    }, [person, groupedFilters]);

    const groupedFound = useMemo(() => {
      if (nlpPeopleGroups.length === 0) return groupedFilters;

      return Object.keys(groupedFilters).reduce((acc, curr) => {
        if (!nlpPeopleGroups.includes(curr)) {
          acc[curr] = groupedFilters[curr];
        }
        return acc;
      }, {});
    }, [groupedFilters, nlpPeopleGroups]);

    const handleShowMoreClick = () => {
      setCurrentlyShowing(currentlyShowing + 5);
    };

    if (Object.keys(groupedFound).length === 0) return null;

    const pulseGroupKey = Object.keys(groupedFound).find((key) => key.toLowerCase() === 'pulse');
    let pulseMatch = undefined;

    if (pulseGroupKey) {
      const pulseDataTypes = groupedFound[pulseGroupKey];
      pulseMatch = pulseDataTypes.find((dt) =>
        currentPersonUuid.matched_detail.some(
          (detail) =>
            detail.name === dt.name && detail.type === oldModelsToNewSelectedCriteria[dt.datatype]
        )
      );
    }

    const finalGroupedFound = Object.keys(groupedFound)
      .filter((key) => !(pulseIsPresent && key.toLowerCase() === 'pulse'))
      .map((key) => ({
        key,
        dataTypes: groupedFound[key].filter(
          (dt) =>
            dt.datatype !== 'clients' && dt.datatype !== 'industries' && dt.datatype !== 'companies'
        ),
      }))
      .filter((group) => {
        if (group.dataTypes.length === 0) return false;
        const allNoMatch =
          group.dataTypes.length > 0 &&
          group.dataTypes.every(
            (dt) =>
              !currentPersonUuid.matched_detail.some(
                (detail) =>
                  detail.name === dt.name &&
                  detail.type === oldModelsToNewSelectedCriteria[dt.datatype]
              )
          );
        if (allNoMatch) return false;
        return true;
      });

    return (
      <CollectiveDirectoryPersonDrawerSearchConnectionsWrapper>
        <CollectiveDirectoryPersonDrawerSearchConnectionsTitle>
          <span>WHY YOU'RE SEEING {person.info.name.split(' ')[0]}</span>
          {person.last_data_import && (
            <InfoTooltip
              size={22}
              fill={'black'}
              text={`Work experience and account holder status is based on data received from ${person.info.company_name}. ${person.info.company_name} last shared data in ${person.last_data_import}. Updates are processed on kyu OS every month.`}
              maxWidth="210px"
            />
          )}
        </CollectiveDirectoryPersonDrawerSearchConnectionsTitle>
        {person?.matches?.industries?.length > 0 && (
          <>
            <CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                KEYWORDS:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                MATCH:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              {person?.matches?.industries?.map((industry) => (
                <>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                      <SearchIconSmall width={15} height={15} fill="var(--color-primary)" />
                      <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                        {industry.match.group || industry.keyword}
                      </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                      <DataTypePill
                        title={
                          typeof industry.match.name === 'string'
                            ? industry.match.name
                            : String(industry.match.name)
                        }
                        type={industry.match.type}
                        slug={industry.match.slug}
                        clickable={false}
                      />
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                  </CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                  <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRowBreakdown
                    match={industry}
                    person={person}
                  />
                </>
              ))}
            </CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
          </>
        )}
        {person.matches.clients.length > 0 && (
          <>
            <CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                KEYWORDS:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                MATCH:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              {person.matches.clients.map((client) => (
                <>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                      <SearchIconSmall width={15} height={15} fill="var(--color-primary)" />
                      <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                        {client.match.group || client.keyword}
                      </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                      <DataTypePill
                        title={
                          typeof client.match.name === 'string'
                            ? client.match.name
                            : String(client.match.name)
                        }
                        type={client.match.type}
                        slug={client.match.slug}
                        clickable={false}
                      />
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                  </CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                  {client.details.experience ? (
                    <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRowBreakdown
                      match={client}
                      person={person}
                    />
                  ) : null}
                </>
              ))}
            </CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
          </>
        )}
        {person.matches.companies.length > 0 && (
          <>
            <CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                KEYWORDS:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                MATCH:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              {person.matches.companies.map((company) => (
                <>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                      <SearchIconSmall width={15} height={15} fill="var(--color-primary)" />
                      <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                        {company.match.group || company.keyword}
                      </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                      <DataTypePill
                        title={
                          typeof company.match.name === 'string'
                            ? company.match.name
                            : String(company.match.name)
                        }
                        type={company.match.type}
                        slug={company.match.slug}
                        clickable={false}
                      />
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                  </CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                  {company.details.experience ? (
                    <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRowBreakdown
                      match={company}
                      person={person}
                    />
                  ) : null}
                  {pulseMatch && pulseIsPresent && (
                    <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownContainer>
                      <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
                        <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
                          <KeyIcon fill="rgba(23, 28, 51, 0.5)" width={15} height={15} />
                          <PulseBlackIcon />
                        </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
                        <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
                          {person.info.name.split(' ')[0]} is a Pulse lead
                        </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
                      </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
                      <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
                        <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
                          {person.info.pulse_description}
                        </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
                      </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
                    </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownContainer>
                  )}
                </>
              ))}
            </CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
          </>
        )}

        {finalGroupedFound.length > 0 && (
          <div key="final-grouped-found-section">
            <CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                KEYWORDS:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
                MATCH:
              </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              {finalGroupedFound
                .map((group) => {
                  const allNoMatch =
                    group.dataTypes.length > 0 &&
                    group.dataTypes.every(
                      (dt) =>
                        !currentPersonUuid.matched_detail.some(
                          (detail) =>
                            detail.name === dt.name &&
                            detail.type === oldModelsToNewSelectedCriteria[dt.datatype]
                        )
                    );
                  if (allNoMatch) return null;
                  return (
                    <CollectiveDirectoryPersonDrawerSearchConnectionsRow key={group.key}>
                      <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                        <SearchIconSmall width={15} height={15} fill="var(--color-primary)" />
                        <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                          {group.key}
                        </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                      </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                      <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRow
                        dataTypes={group.dataTypes}
                        person={currentPersonUuid}
                      />
                    </CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                  );
                })
                .filter(Boolean)}
            </CollectiveDirectoryPersonDrawerSearchConnectionsGrid>

            {finalGroupedFound.length > currentlyShowing && (
              <CollectiveDirectoryPersonDrawerSearchConnectionsRowShowMore
                onClick={handleShowMoreClick}
              >
                SHOW MORE
              </CollectiveDirectoryPersonDrawerSearchConnectionsRowShowMore>
            )}
          </div>
        )}
      </CollectiveDirectoryPersonDrawerSearchConnectionsWrapper>
    );
  };

CollectiveDirectoryPersonDrawerSearchMatches.Matches =
  function CollectiveDirectoryPersonDrawerSearchMatchesMatches({
    person,
  }: {
    person: ICollectiveDirectoryPersonDrawerResponse;
  }) {
    return (
      <>
        {person?.matches?.industries?.length > 0 && (
          <CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
            <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              KEYWORDS:
            </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
            <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              MATCH:
            </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
            {person?.matches?.industries?.map((industry) => (
              <>
                <CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <SearchIconSmall width={15} height={15} fill="var(--color-primary)" />
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                      {industry.match.group || industry.keyword}
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                  </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <DataTypePill
                      title={
                        typeof industry.match.name === 'string'
                          ? industry.match.name
                          : String(industry.match.name)
                      }
                      type={industry.match.type}
                      slug={industry.match.slug}
                      clickable={false}
                    />
                  </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                </CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRowBreakdown
                  match={industry}
                  person={person}
                />
              </>
            ))}
          </CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
        )}
        {person?.matches?.clients?.length > 0 && (
          <CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
            <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              KEYWORDS:
            </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
            <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              MATCH:
            </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
            {person?.matches?.clients?.map((client) => (
              <>
                <CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <SearchIconSmall width={15} height={15} fill="var(--color-primary)" />
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                      {client.match.group || client.keyword}
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                  </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <DataTypePill
                      title={
                        typeof client.match.name === 'string'
                          ? client.match.name
                          : String(client.match.name)
                      }
                      type={client.match.type}
                      slug={client.match.slug}
                      clickable={false}
                    />
                  </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                </CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                {client.details.experience ? (
                  <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRowBreakdown
                    match={client}
                    person={person}
                  />
                ) : null}
              </>
            ))}
          </CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
        )}
        {person?.matches?.companies?.length > 0 && (
          <CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
            <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              KEYWORDS:
            </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
            <CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
              MATCH:
            </CollectiveDirectoryPersonDrawerSearchConnectionsColumnTitle>
            {person?.matches?.companies?.map((company) => (
              <>
                <CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <SearchIconSmall width={15} height={15} fill="var(--color-primary)" />
                    <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                      {company.match.group || company.keyword}
                    </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitle>
                  </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                  <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                    <DataTypePill
                      title={
                        typeof company.match.name === 'string'
                          ? company.match.name
                          : String(company.match.name)
                      }
                      type={company.match.type}
                      slug={company.match.slug}
                      clickable={false}
                    />
                  </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordContainer>
                </CollectiveDirectoryPersonDrawerSearchConnectionsRow>
                {company.details.experience ? (
                  <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRowBreakdown
                    match={company}
                    person={person}
                  />
                ) : null}
              </>
            ))}
          </CollectiveDirectoryPersonDrawerSearchConnectionsGrid>
        )}
      </>
    );
  };

CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRow =
  function CollectiveDirectoryPersonDrawerSearchConnectionsRow({
    dataTypes,
    person,
  }: {
    dataTypes: {
      aliases: string[];
      name: string;
      grouping: string;
      original_name: string;
      datatype: string;
    }[];
    person: IPeopleUuids;
  }) {
    const [currentlyShowing, setCurrentlyShowing] = useState<number>(3);

    const handleShowMoreClick = () => {
      setCurrentlyShowing(currentlyShowing + 3);
    };

    const filteredDataTypes = useMemo(() => {
      const filtered = dataTypes.filter((dataType) => {
        return person.matched_detail.find(
          (detail) =>
            detail.name === dataType.name &&
            detail.type === oldModelsToNewSelectedCriteria[dataType.datatype]
        );
      });

      const filteredByName = filtered.sort((a, b) => {
        const aIncluded = person.matched_elements.includes(a.name);
        const bIncluded = person.matched_elements.includes(b.name);
        return aIncluded && !bIncluded ? -1 : !aIncluded && bIncluded ? 1 : 0;
      });

      return filteredByName;
    }, [dataTypes, person]);

    if (filteredDataTypes.length === 0)
      return (
        <CollectiveDirectoryPersonDrawerSearchConnectionsColumn>
          <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitleNoMatch>
            <CloseIcon width={8} height={8} fill="rgba(23, 28, 51, 0.3)" fillOpacity="1" />
            <CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitleNoMatchText>
              No match
            </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitleNoMatchText>
          </CollectiveDirectoryPersonDrawerSearchConnectionsKeywordTitleNoMatch>
        </CollectiveDirectoryPersonDrawerSearchConnectionsColumn>
      );

    return (
      <CollectiveDirectoryPersonDrawerSearchConnectionsColumn>
        {filteredDataTypes.slice(0, currentlyShowing).map((dataType, index) => (
          <Tooltip
            key={`${dataType.name}-${index}`}
            maxWidth="157px"
            text={`We found the ${
              oldModelsToNewSelectedCriteriaTooltipLabel[dataType.datatype]
            }, '${dataType.name}', in ${person.name.split(' ')[0]}'s profile.`}
            disableInMobile
          >
            <DataTypePill
              title={dataType.name}
              type={oldModelsToNewSelectedCriteria[dataType.datatype]}
              clickable={false}
            />
          </Tooltip>
        ))}
        {filteredDataTypes.length > currentlyShowing && (
          <CollectiveDirectoryPersonDrawerSearchConnectionsColumnShowMore
            onClick={handleShowMoreClick}
          >
            <CollectiveDirectoryPersonDrawerSearchConnectionsColumnShowMoreText>
              ... and {filteredDataTypes.length - currentlyShowing} more
            </CollectiveDirectoryPersonDrawerSearchConnectionsColumnShowMoreText>
          </CollectiveDirectoryPersonDrawerSearchConnectionsColumnShowMore>
        )}
      </CollectiveDirectoryPersonDrawerSearchConnectionsColumn>
    );
  };

CollectiveDirectoryPersonDrawerSearchMatches.SearchConnectionsRowBreakdown =
  function CollectiveDirectoryPersonDrawerSearchConnectionsRowBreakdown({
    match,
    person,
  }: {
    match: Match;
    person: ICollectiveDirectoryPersonDrawerResponse;
  }) {
    return (
      <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownContainer>
        {match.details.experience.account_holder.length > 0 ? (
          <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
            <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
              <KeyIcon fill="rgba(23, 28, 51, 0.5)" width={15} height={15} />
            </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
            <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
              {person.info.name.split(' ')[0]} is an account holder for{' '}
              {arrayToOxford(match.details.experience.account_holder)}.
            </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
          </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
        ) : null}
        {match.details.experience.score > 0 ? (
          <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
            <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
              <CubeIcon fill="rgba(23, 28, 51, 0.5)" width={15} height={15} />
            </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
            <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
              {person.info.name.split(' ')[0]} has{' '}
              {experienceMonthsToYears(match.details.experience.score)} XP with{' '}
              {match.match.type == 'industry' ? 'clients connected to the' : ''}{' '}
              {typeof match.match.name === 'string' ? match.match.name : String(match.match.name)}
              {match.match.type == 'industry' && ' industry'}
              {match.match.type == 'industry' &&
                match.details.experience.project_status.active_projects.length > 0 &&
                `. Currently working `}
              {match.match.type == 'industry' &&
                match.details.experience.project_status.active_projects.length > 0 && (
                  <>
                    {' on '}
                    {htmlElementsArrayToOxford(
                      match.details.experience.project_status.active_projects.map((project) => (
                        <Link
                          key={project.project_slug}
                          style={{ color: 'rgb(159, 124, 251)' }}
                          to={`/project/${project.project_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {project.project_name}
                        </Link>
                      )),
                      true
                    )}
                    {' with '}
                    {arrayToOxford(
                      Array.from(
                        new Set(
                          match.details.experience.project_status.active_projects.map(
                            (project) => project.client_name
                          )
                        )
                      )
                    )}
                  </>
                )}
              {match.match.type == 'client' &&
                match.details.experience.project_status.active_projects.length > 0 &&
                `. Currently working `}
              {match.match.type == 'client' &&
                match.details.experience.project_status.active_projects.length > 0 && (
                  <>
                    {' on '}
                    {htmlElementsArrayToOxford(
                      match.details.experience.project_status.active_projects.map((project) => (
                        <Link
                          key={project.project_slug}
                          style={{ color: 'rgb(159, 124, 251)' }}
                          to={`/project/${project.project_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {project.project_name}
                        </Link>
                      )),
                      true
                    )}
                  </>
                )}
              {match.match.type == 'client' &&
                match.details.experience.project_status.active_projects.length > 0 &&
                ` with them`}
              .
            </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
          </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
        ) : null}
        {match.details.experience.prior_experience == true ? (
          <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
            <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
              <CubeIcon fill="rgba(23, 28, 51, 0.5)" width={15} height={15} />
            </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemIcon>
            <CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
              {match.match.type == 'industry'
                ? `${person.info.name.split(' ')[0]} has work experience in the ${
                    typeof match.match.name === 'string'
                      ? match.match.name
                      : String(match.match.name)
                  } industry prior to joining ${person.info.company_name}.`
                : `${person.info.name.split(' ')[0]} has worked with ${
                    typeof match.match.name === 'string'
                      ? match.match.name
                      : String(match.match.name)
                  } in a previous role.`}
            </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItemDescription>
          </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownItem>
        ) : null}
      </CollectiveDirectoryPersonDrawerSearchConnectionBreakdownContainer>
    );
  };
